/**
 * Tiny deterministic colour primitives — no dependencies.
 *
 * Just enough for Workspace Canvas Appearance:
 *   - hex <-> rgb
 *   - WCAG relative luminance (perceptual "how bright does this read")
 *   - capLuminance: darken a colour to a maximum relative luminance while
 *     preserving its hue/chroma exactly (scales the linear-light channels)
 *   - mix: straight sRGB channel lerp
 *
 * Not a colour framework. If a real colour system is ever needed elsewhere,
 * replace this — nothing here is load-bearing beyond appearance.
 */

function clamp(n, lo, hi) {
  return n < lo ? lo : n > hi ? hi : n;
}

/** `true` for `#rgb` / `#rrggbb` (case-insensitive, `#` optional). */
export function isValidHexColor(value) {
  return typeof value === 'string' && /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());
}

/** `'#rrggbb'` / `'#rgb'` / no-`#` -> `{ r, g, b }` (0..255 ints), or `null` if invalid. */
export function hexToRgb(value) {
  if (!isValidHexColor(value)) return null;
  let h = value.trim().replace(/^#/, '').toLowerCase();
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/** `{ r, g, b }` (any real numbers) -> `'#rrggbb'` (lowercase, clamped, rounded). */
export function rgbToHex({ r, g, b }) {
  const to2 = (n) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

/** One sRGB channel (0..255) -> linear-light (0..1). */
export function srgbToLinear(channel) {
  const s = clamp(channel, 0, 255) / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** Linear-light (0..1) -> sRGB channel (0..255, rounded). */
export function linearToSrgb(linear) {
  const l = clamp(linear, 0, 1);
  const s = l <= 0.0031308 ? l * 12.92 : 1.055 * l ** (1 / 2.4) - 0.055;
  return Math.round(clamp(s, 0, 1) * 255);
}

/** WCAG relative luminance (0..1) of an `{ r, g, b }` colour. */
export function relativeLuminance({ r, g, b }) {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/**
 * Darken `hex` until its relative luminance is `<= maxLuminance`, preserving
 * hue and chroma exactly (all three linear-light channels scaled by the same
 * factor). A colour already at/under the cap is returned unchanged (only
 * re-serialised). Idempotent within one rounding step.
 */
export function capLuminance(hex, maxLuminance) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const L = relativeLuminance(rgb);
  if (!(L > maxLuminance + 1e-9) || L <= 0) return rgbToHex(rgb);
  const factor = maxLuminance / L;
  return rgbToHex({
    r: linearToSrgb(srgbToLinear(rgb.r) * factor),
    g: linearToSrgb(srgbToLinear(rgb.g) * factor),
    b: linearToSrgb(srgbToLinear(rgb.b) * factor),
  });
}

/** Per-channel sRGB lerp between two hex colours; `t` in 0..1 (clamped). */
export function mix(hexA, hexB, t) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return a ? rgbToHex(a) : hexB;
  const k = clamp(t, 0, 1);
  return rgbToHex({
    r: a.r + (b.r - a.r) * k,
    g: a.g + (b.g - a.g) * k,
    b: a.b + (b.b - a.b) * k,
  });
}
